import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";

import CharacterCharacteristicInput from "@dndComponents/character/features/CharacterCharacteristicInput";

interface Props {
  character: Character;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterDescription({
  character,
}: Props) {
  const [view, setView] = useState("ALL");
  const [base_size, setBaseSize] = useState(character.base_size);
  const [characteristics, setCharacteristics] = useState(character.characteristics);

  const renderCharacteristicFeatures = () => {
    if (view === "ALL") {
      return character.characteristic_feature_source_names.map(source_name => {
        return renderCharacteristicFeaturesForSourceName(source_name);
      });
    } else if (character.characteristic_feature_source_names.includes(view)) {
      return renderCharacteristicFeaturesForSourceName(view);
    }
    return null;
  }

  const renderCharacteristicFeaturesForSourceName = (source_name: string) => {
    const characteristic_features = character.characteristic_features;
    const header = 
      <Grid
        key={`${source_name}_header`}
        style={{ fontWeight: "bold", fontSize: "12px" }}>
        { source_name }
      </Grid>;
    const return_us = characteristic_features.filter(o => o.source_name === source_name).map((cf, key) => {
      return (
        <CharacterCharacteristicInput key={`${source_name}_${key}`}
          character={character}
          char_feature={cf}
          // value={cf.feature_options[0]}
          onChange={(changed: string) => {
            cf.feature_options[0] = changed;
            character.changed();
          }}
        />
      );
    });
    return [header,...return_us];
  }
  
  const enabled = api.user && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id));
  const menu_options = ["ALL","Characteristics","Appearance",...character.characteristic_feature_source_names];
  return (
    <Grid
      container
      spacing={0}
      direction="column"
      className="curved_border bg_black_transp"
      style={{
        fontSize: "11px"
      }}>
      <Grid className="centered header">
          Description
      </Grid>
      <Grid>
        <div className="centered">
          <CenteredMenu
            options={menu_options}
            selected={view}
            onSelect={setView}
          />
        </div>
      </Grid>
      { (view === "ALL" || view === "Characteristics") && 
        <>
          <Grid style={{ fontWeight: "bold", fontSize: "12px" }}>
            Characteristics
          </Grid>
          { character.race.source && 
            <>
              <Grid>
                <span style={{ fontWeight: "bold" }}>Creature Type: { character.race.source.creature_type }</span>
              </Grid>
              <Grid>
                { character.race.source.sizes.length === 1 ?
                  <span style={{ fontWeight: "bold" }}>Size: { common_util.size_map[base_size] }</span>
                :
                  <SelectStringBox 
                    name="Size"
                    options={common_util.get_size_strings(character.race.source.sizes)}
                    value={common_util.size_map[base_size]} 
                    disabled={ !enabled }
                    onChange={(value: string) => {
                      const new_base_size = common_util.size_to_num_map[value];
                      setBaseSize(new_base_size);
                      character.base_size = new_base_size;
                      character.changed();
                    }}
                  />
                }
              </Grid>
            </>
          }
          <Grid>
            <StringBox
              name="Faith"
              disabled={ !enabled }
              value={characteristics.faith}
              onBlur={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.faith = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <SelectStringBox
              name="Alignment"
              disabled={ !enabled }
              options={["Lawful Good","Lawful Neutral","Lawful Evil","Neutral Good","Neutral","Neutral Evil","Chaotic Good","Chaotic Neutral","Chaotic Evil"]}
              value={characteristics.alignment}
              onChange={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.alignment = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <SelectStringBox
              name="Lifestyle"
              disabled={ !enabled }
              options={["Wretched","Squalid (1 SP)","Poor (2 SP)","Modest (1 GP)","Comfortable (2 GP)","Wealthy (4 GP)","Aristocratic (10 GP Min)"]}
              value={characteristics.lifestyle}
              onChange={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.lifestyle = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <StringBox
              name="Hair"
              disabled={ !enabled }
              value={characteristics.hair}
              onBlur={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.hair = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <StringBox
              name="Skin"
              disabled={ !enabled }
              value={characteristics.skin}
              onBlur={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.skin = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <StringBox
              name="Eyes"
              disabled={ !enabled }
              value={characteristics.eyes}
              onBlur={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.eyes = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <StringBox
              name="Height"
              disabled={ !enabled }
              value={characteristics.height}
              onBlur={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.height = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <StringBox
              name="Weight (lbs)"
              disabled={ !enabled }
              value={characteristics.weight}
              type="number"
              onBlur={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.weight = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <StringBox
              name="Age (Years)"
              disabled={ !enabled }
              value={characteristics.age}
              type="number"
              onBlur={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.age = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
          <Grid>
            <SelectStringBox
              name="Gender"
              disabled={ !enabled }
              options={["Male","Female","Other"]}
              value={characteristics.gender}
              onChange={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.gender = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
        </>
      }
      { (view === "ALL" || view === "Appearance") && 
        <>
          <Grid style={{ fontWeight: "bold", fontSize: "12px" }}>
            Appearance
          </Grid>
          <Grid>
            <StringBox 
              name="Appearance" 
              disabled={ !enabled }
              value={characteristics.appearance} 
              multiline
              onBlur={(changed: string) => {
                const new_characteristics = characteristics;
                new_characteristics.appearance = changed;
                setCharacteristics(new_characteristics);
                character.characteristics = characteristics;
                character.changed();
              }}
            />
          </Grid>
        </>
      }
      { renderCharacteristicFeatures() }
    </Grid>
  );
}
